/**
 * Utility functions for parsing and formatting time inputs for labour line items
 */

/**
 * Parse various time input formats and convert to decimal hours
 * Supported formats:
 * - 1:30 → 1.5 (hours:minutes)
 * - 1.5 → 1.5 (decimal hours)
 * - 90m → 1.5 (minutes only)
 * - 2h → 2.0 (hours only)
 * - 1h 30m → 1.5 (full format)
 *
 * @param {string} input - The time input string
 * @returns {number} - Decimal hours, or 0 if invalid
 */
export const parseTimeInput = (input) => {
  if (!input || typeof input !== "string") {
    return 0;
  }

  const trimmedInput = input.trim().toLowerCase();

  if (!trimmedInput) {
    return 0;
  }

  // Match patterns like "1:30" (hours:minutes)
  const hhmmMatch = trimmedInput.match(/^(\d+):(\d+)$/);
  if (hhmmMatch) {
    const hours = parseInt(hhmmMatch[1], 10);
    const minutes = parseInt(hhmmMatch[2], 10);

    // Validate minutes (0-59)
    if (minutes >= 60) {
      return 0; // Invalid format
    }

    return hours + minutes / 60;
  }

  // Match patterns like "90m" (minutes only)
  const minutesMatch = trimmedInput.match(/^(\d+(?:\.\d+)?)m$/);
  if (minutesMatch) {
    const totalMinutes = parseFloat(minutesMatch[1]);
    return totalMinutes / 60;
  }

  // Match patterns like "2h" (hours only)
  const hoursMatch = trimmedInput.match(/^(\d+(?:\.\d+)?)h$/);
  if (hoursMatch) {
    return parseFloat(hoursMatch[1]);
  }

  // Match patterns like "1h 30m" (full format)
  const fullMatch = trimmedInput.match(/^(\d+(?:\.\d+)?)h\s*(\d+(?:\.\d+)?)m$/);
  if (fullMatch) {
    const hours = parseFloat(fullMatch[1]);
    const minutes = parseFloat(fullMatch[2]);

    // Validate minutes (0-59)
    if (minutes >= 60) {
      return 0; // Invalid format
    }

    return hours + minutes / 60;
  }

  // Match decimal numbers (e.g., "1.5")
  const decimalMatch = trimmedInput.match(/^(\d+(?:\.\d+)?)$/);
  if (decimalMatch) {
    return parseFloat(decimalMatch[1]);
  }

  return 0; // Invalid format
};

/**
 * Convert decimal hours to "Xh Ym" format
 *
 * @param {number} decimalHours - Decimal hours value
 * @returns {string} - Formatted time string (e.g., "1h 30m", "2h 0m")
 */
export const formatDecimalToTime = (decimalHours) => {
  if (typeof decimalHours !== "number" || Number.isNaN(decimalHours) || decimalHours < 0) {
    return "0h 0m";
  }

  const totalMinutes = Math.round(decimalHours * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${hours}h ${minutes}m`;
};

/**
 * Validate if the input string matches any of the supported time formats
 *
 * @param {string} input - The time input string
 * @returns {boolean} - True if valid format, false otherwise
 */
export const validateTimeFormat = (input) => {
  if (!input || typeof input !== "string") {
    return false;
  }

  const trimmedInput = input.trim().toLowerCase();

  if (!trimmedInput) {
    return false;
  }

  // Check all supported patterns
  const patterns = [
    /^(\d+):(\d+)$/, // 1:30
    /^(\d+(?:\.\d+)?)$/, // 1.5
    /^(\d+(?:\.\d+)?)m$/, // 90m
    /^(\d+(?:\.\d+)?)h$/, // 2h
    /^(\d+(?:\.\d+)?)h\s*(\d+(?:\.\d+)?)m$/, // 1h 30m
  ];

  for (const pattern of patterns) {
    const match = trimmedInput.match(pattern);
    if (match) {
      // Additional validation for minutes in hours:minutes format
      if (pattern.source.includes(":") && match[2]) {
        const minutes = parseInt(match[2], 10);
        if (minutes >= 60) {
          return false;
        }
      }

      // Additional validation for minutes in "Xh Ym" format
      if (pattern.source.includes("h\\s*") && match[2]) {
        const minutes = parseFloat(match[2]);
        if (minutes >= 60) {
          return false;
        }
      }

      return true;
    }
  }

  return false;
};

/**
 * Get format examples for user guidance
 *
 * @returns {string[]} - Array of format examples
 */
export const getTimeFormatExamples = () => {
  return ["1:30 (1h 30m)", "1.5 (1h 30m)", "90m (1h 30m)", "2h (2h 0m)", "1h 30m (1h 30m)"];
};

/**
 * Convert time format display back to decimal for storage
 * This is mainly for parsing the formatted display value back to decimal
 *
 * @param {string} timeDisplay - Time in "Xh Ym" format
 * @returns {number} - Decimal hours
 */
export const parseFormattedTime = (timeDisplay) => {
  if (!timeDisplay || typeof timeDisplay !== "string") {
    return 0;
  }

  const match = timeDisplay.match(/^(\d+)h\s*(\d+)m$/);
  if (match) {
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    return hours + minutes / 60;
  }

  return 0;
};
